<script setup lang="ts">
import { computed } from "vue";
import { datetimeWithDayOfWeek } from "../plugins/Formatting";
import router from "../router";
import { RouterLinkProps } from "vue-router";

import { useAuth } from "../lib/useAuth";
import { useRfpPhases } from "../lib/useRfp";

import {
  Proposal,
  downloadSubmittedAttachments,
  downloadProposalData,
} from "@/lib/useProposal";
import { RfpCore } from "@/types/Marketplace";
import { RouteLocationRaw } from "vue-router";
import { usePolicyProject } from "@/lib/usePolicy";
import { getAccessToken } from "@/lib/useAccessToken";
import env from "@/env";
import { useToast } from "@/lib/useToast";

const props = defineProps<{
  rfp: RfpCore;
  proposal?: Proposal;
  requestType: string;
  hasParts: boolean;
  propsLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: "start-proposal"): void;
  (e: "create-parts"): void;
}>();

const today = new Date();

type TimelineAction = {
  text: string;
  pastText?: string;
  icon: string;
  hideAfterEnd?: boolean;
  // TODO: remove this later when we have a "proposal_evaluation" phase to put the RFP analysis action
  isSpecialWorkaroundForRfpAnalysisPage?: boolean;
  link?: RouteLocationRaw;
  routerLinkProps?: Partial<RouterLinkProps>;
  onClick?: () => void;
};

type TimelineItem = {
  id: string;
  name: string;
  start: Date;
  end: Date;
  description: string;
  actions: TimelineAction[];
};

const { isAuthed, isProvider, isBuyer, isSuper } = useAuth();
const { pushToast } = useToast();

const reactiveRfpId = computed(() => props.rfp.id);
const {
  phases,
  activePhases,
  loading: phasesLoading,
} = useRfpPhases(reactiveRfpId);
const reactiveRfp = computed(() => props.rfp);

const { ownsRFP, canManageRfp } = usePolicyProject(reactiveRfp);
const isOtherBuyer = computed(() => isBuyer.value && !ownsRFP.value);

async function createPartsAndRedirect() {
  emit("create-parts");
  router.push({
    name: "proposal-builder",
    query: {
      rfp_id: props.rfp.id,
    },
  });
}

function downloadProposalTemplate() {
  // see notes in useFormResponse downloadAttachment for more info
  const accessToken = getAccessToken();
  let url = `${env.apiBaseUrl}/rfps/${reactiveRfp.value.id}/proposal_template`;
  if (accessToken) {
    url = `${url}?access_token=${encodeURIComponent(accessToken)}`;
  } else {
    // Shouldn't happen, but just in case
    pushToast({
      message:
        "You must be logged in as a Station A member to download the proposal template",
      level: "error",
    });
  }
  window.open(url, "_blank");
}

function getPreLaunchRfpTimelineItem(start: Date, end: Date): TimelineItem {
  /*
   Pre-RFP open phase
   - This is the phase available before the RFP is open
   - Actions only available to super users and owners of the RFP/Q
   - Includes actions:
     - See disclosures
     - Preview proposal forms
     - Create/Edit proposal forms
   */
  const preRfpActions = [];
  preRfpActions.push(
    // StA/Buyer owners should be able to see disclosures and preview the proposal forms
    {
      text: "See disclosures",
      icon: "drive_file_move",
      link: {
        name: "rfp",
        params: { rfpId: props.rfp.id },
        hash: "#disclosures",
      },
      routerLinkProps: { replace: true },
    },
  );
  if (props.hasParts) {
    preRfpActions.push({
      text: "Preview proposal forms",
      icon: "visibility",
      link: {
        name: "proposal-preview",
        query: {
          rfp_id: props.rfp.id,
        },
      },
    });
  }
  if (isSuper.value) {
    if (props.hasParts) {
      preRfpActions.push({
        text: "Edit proposal forms",
        icon: "tune",
        link: {
          name: "proposal-builder",
          query: {
            rfp_id: props.rfp.id,
          },
        },
      });
      preRfpActions.push({
        text: "Download proposal template",
        icon: "deployed_code_update",
        onClick: downloadProposalTemplate,
      });
    } else {
      preRfpActions.push({
        text: "Create proposal forms",
        icon: "assignment_add",
        onClick: createPartsAndRedirect,
      });
    }
  }

  return {
    id: "pre_launch_period",
    name: `Pre-launch period`,
    start: start,
    end: end,
    description: `Set up the ${props.requestType} materials before it opens.`,
    actions: preRfpActions,
  };
}

function getQATimelineItem(start: Date, end: Date): TimelineItem {
  /*
   RFP/Q Open (RFI period)
   - This phase is available from the RFP open date til the accepting proposals date
   - Everyone sees this timeline item, but may see different action items
   - Providers should see the items:
     - See disclosures
     - Ask/See questions (depending on if the end time of this timeline item has passed)
   - Buyers should see the items (we can also restrict to only the buyer owner of the RFP):
     - See questions
   */
  const rfpOpenActions =
    isProvider.value || !isAuthed.value
      ? [
          {
            text: "See disclosures", // the buyer owner and StA user's can access this in the previous timeline item, so we don't need to show it again to them
            icon: "drive_file_move",
            link: {
              name: "rfp",
              params: { rfpId: props.rfp.id },
              hash: "#disclosures",
            },
            routerLinkProps: { replace: true },
          },
          {
            text: "Ask questions",
            pastText: "See questions",
            icon: "forum",
            link: {
              name: "rfp",
              params: { rfpId: props.rfp.id },
              hash: "#questions-answers",
            },
            routerLinkProps: { replace: true },
          },
        ]
      : [
          {
            text: "See questions",
            icon: "forum",
            link: {
              name: "rfp",
              params: { rfpId: props.rfp.id },
              hash: "#questions-answers",
            },
            routerLinkProps: { replace: true },
          },
        ];

  const rfpOpenDescription = ownsRFP.value
    ? "Providers can now see the project details and submit questions. View their questions by clicking “See questions” below"
    : "View disclosures to familiarize yourself with the project and ask any questions using the “Submit New Question” button in the Q&A tab. Be sure to check back on the Q&A tab regularly, which hosts answers to submitted questions.";
  return {
    id: "qa_period",
    name: `${props.requestType} Q&A period`,
    start: start,
    end: end,
    description: rfpOpenDescription,
    actions: rfpOpenActions,
  };
}

function getFirstRoundSubmissionTimelineItem(
  start: Date,
  end: Date,
): TimelineItem {
  /*
   RFP/Q accepting proposals
   - This phase is open from the accepting proposals start date to the RFP close date
   - Everyone sees this timeline item, but might see different actions
   - Providers should see:
     - Start/Resume/View submitted proposal (depending on the proposal state)
     - Preview proposal forms
   */
  const acceptingProposalsActions: TimelineAction[] = [];
  if (isProvider.value || !isAuthed.value) {
    if (props.proposal && props.proposal.submitted) {
      acceptingProposalsActions.push({
        text: "View submitted proposal",
        icon: "task",
        link: {
          name: "proposal",
          params: { proposalId: props.proposal.id },
        },
      });
    } else if (props.proposal) {
      acceptingProposalsActions.push({
        text: "Resume proposal",
        icon: "edit_document",
        pastText: "View unsubmitted proposal",
        link: {
          name: "proposal",
          params: { proposalId: props.proposal.id },
        },
      });
    } else {
      acceptingProposalsActions.push({
        text: "Start proposal",
        icon: "note_add",
        hideAfterEnd: true,
        onClick: () => {
          emit("start-proposal");
        },
      });
    }
    acceptingProposalsActions.push({
      text: "Preview proposal forms",
      icon: "visibility",
      link: {
        name: "proposal-preview",
        query: {
          rfp_id: props.rfp.id,
        },
      },
    });
  }

  if (isSuper.value) {
    acceptingProposalsActions.push({
      text: "Download attachments",
      icon: "download",
      onClick: () => downloadSubmittedAttachments(props.rfp.id),
    });
    acceptingProposalsActions.push({
      text: "Download proposal data",
      icon: "all_inbox",
      onClick: () => downloadProposalData(props.rfp.id),
    });
    acceptingProposalsActions.push({
      text: "Build presentation",
      icon: "handyman",
      link: {
        name: "presentation-builder",
        params: {
          rfp_id: props.rfp.id,
        },
      },
    });
  }

  if (isSuper.value || ownsRFP.value) {
    acceptingProposalsActions.push({
      text: "View analysis",
      icon: "query_stats",
      isSpecialWorkaroundForRfpAnalysisPage: true,
      link: {
        name: "rfp-analysis",
        params: {
          rfpId: props.rfp.id,
        },
      },
    });
  }

  const acceptingProposalsDescription = ownsRFP.value
    ? "Providers are now submitting proposals. Once the submission period ends, you will be able to view their submitted proposals."
    : "When you are ready to view proposal questions and start your response, click “Start Proposal” below. The Q&A is now closed and all answered questions are posted to the Q&A tab.";

  return {
    id: "first_round_submission_period",
    name: `Proposal submission period`,
    start: start,
    end: end,
    description: acceptingProposalsDescription,
    actions: acceptingProposalsActions,
  };
}

function getShortlistSubmissionTimelineItem(
  start: Date,
  end: Date,
): TimelineItem | undefined {
  /*
    RFP shortlist period
    - This is available from the RFP close date to the shortlist deadline
    - This is only visible to providers with shortlisted proposals, and the shortlist deadline has been set on the RFP
    - Shortlisted providers should see the following actions:
      - Refine/View submitted proposal (depending on proposal state)
    */
  if (props.proposal && props.proposal.shortlisted) {
    const shortlistOpenActions = isProvider.value
      ? [
          {
            text: props.proposal.shortlistSubmitted
              ? "View submitted proposal"
              : "Refine proposal",
            icon: props.proposal.shortlistSubmitted ? "task" : "edit_document",
            hideAfterEnd: true,
            link: {
              name: "proposal",
              params: { proposalId: props.proposal.id },
            },
          },
        ]
      : [];
    const shortlistOpenDescription = ownsRFP.value
      ? "Shortlisted providers are now submitting shortlist proposals. Once the submission period ends, you will be able to view their refined proposals."
      : "Congratulations! Your proposal has been shortlisted. Please submit your shortlist proposal by clicking “Refine Proposal” below. If you submitted a response, your Marketplace Success Rep will be in touch with next steps via email.";

    return {
      id: "shortlist_submission_period",
      name: "Shortlist submission period",
      start: start,
      end: end,
      description: shortlistOpenDescription,
      actions: shortlistOpenActions,
    };
  }
}

function getFixingTimelineItem(start: Date, end: Date): TimelineItem {
  /*
    Proposal fixing period
    - This is the period after the first round submission period closes, and the user
  */
  const fixingActions: TimelineAction[] = [];
  if (props.proposal && props.proposal.submitted) {
    fixingActions.push({
      text: "Fix flagged responses",
      icon: "flag",
      link: {
        name: "proposal",
        params: { proposalId: props.proposal.id },
      },
    });
  }

  const fixingDescription = ownsRFP.value
    ? "Providers are now fixing flagged proposal responses."
    : "During the fixing period, the person who submitted your proposal will receive daily emails if Station A has flagged any of your responses as being potentially erroneous. During this period, you can open your proposal to review and correct any question responses that Station A has flagged.";

  return {
    id: "fixing_period",
    name: `Proposal fixing period`,
    start: start,
    end: end,
    description: fixingDescription,
    actions: fixingActions,
  };
}

function getShortlistFixingTimelineItem(start: Date, end: Date): TimelineItem {
  /*
    Proposal fixing period
    - This is the period after the first round submission period closes, and the user
  */
  const fixingActions: TimelineAction[] = [];
  if (props.proposal && props.proposal.submitted) {
    fixingActions.push({
      text: "Fix flagged responses",
      icon: "flag",
      link: {
        name: "proposal",
        params: { proposalId: props.proposal.id },
      },
    });
  }

  const shortlistFixingDescription = ownsRFP.value
    ? "Providers are now fixing flagged shortlist proposal responses."
    : "If you have submitted a shortlist proposal, Station A has reviewed and flagged any erroneous responses for fixing. Correct them before the deadline so your proposal is evaluated appropriately.";

  return {
    id: "shortlist_fixing_period",
    name: `Shortlist proposal fixing period`,
    start: start,
    end: end,
    description: shortlistFixingDescription,
    actions: fixingActions,
  };
}

const timeline = computed<TimelineItem[]>(() => {
  const timelineItems = [];
  if (phases.value) {
    for (const phase of phases.value) {
      if (phase.phase_type === "pre_launch_period" && canManageRfp.value) {
        timelineItems.push(getPreLaunchRfpTimelineItem(phase.start, phase.end));
      }
      if (phase.phase_type === "qa_period") {
        timelineItems.push(getQATimelineItem(phase.start, phase.end));
      }
      if (phase.phase_type === "first_round_submission_period") {
        timelineItems.push(
          getFirstRoundSubmissionTimelineItem(phase.start, phase.end),
        );
      }
      if (phase.phase_type === "fixing_period") {
        timelineItems.push(getFixingTimelineItem(phase.start, phase.end));
      }
      if (
        phase.phase_type === "shortlist_submission_period" &&
        props.proposal?.shortlisted
      ) {
        const shortlistItem = getShortlistSubmissionTimelineItem(
          phase.start,
          phase.end,
        );
        if (shortlistItem) {
          timelineItems.push(shortlistItem);
        }
      }
      if (
        phase.phase_type === "shortlist_fixing_period" &&
        props.proposal?.shortlisted
      ) {
        const shortlistFixingItem = getShortlistFixingTimelineItem(
          phase.start,
          phase.end,
        );
        if (shortlistFixingItem) {
          timelineItems.push(shortlistFixingItem);
        }
      }
    }
  }

  return timelineItems;
});

async function handleOnClick(onClick?: () => void) {
  if (onClick) {
    await onClick();
  }
}

function filterActions(t: TimelineItem) {
  return t.actions.filter(
    (action) => !(isPastMilestone(t) && action.hideAfterEnd),
  );
}

function isCurrentMilestone(t: TimelineItem) {
  return activePhases.value?.includes(t.id);
}

function isPastMilestone(t: TimelineItem) {
  return t.end ? new Date(t.end) < today : false;
}

function isFutureMilestone(t: TimelineItem) {
  return t.start ? new Date(t.start) > today : false;
}

function getState(t: TimelineItem) {
  if (isCurrentMilestone(t)) return "current";
  if (isPastMilestone(t)) return "past";
  return "future";
}

function getMilestoneProps(t: TimelineItem) {
  if (isCurrentMilestone(t)) {
    return {
      classes: [
        "tw:bg-success-500/50",
        "tw:text-success-700",
        "tw:dark:text-success-200",
      ],
      icon: "event_available",
    };
  } else if (isPastMilestone(t)) {
    return {
      classes: [
        "tw:bg-zinc-500/30",
        "tw:text-zinc-600",
        "tw:dark:text-zinc-300",
      ],
      icon: "event_busy",
    };
  }
  return {
    classes: [
      "tw:bg-yellow-500/30",
      "tw:text-yellow-600",
      "tw:dark:text-yellow-300",
    ],
    icon: "event_upcoming",
  };
}
</script>

<template>
  <div v-if="phasesLoading || propsLoading" class="tw:grid tw:gap-4">
    <Skeleton v-for="i in 3" :key="i" height="12em" />
  </div>
  <Timeline
    v-else
    :value="timeline"
    :pt="{
      eventOpposite: 'tw:hidden',
    }"
  >
    <template #marker="slotProps">
      <div
        class="tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-full"
        :class="getMilestoneProps(slotProps.item).classes"
      >
        <TIcon>{{ getMilestoneProps(slotProps.item).icon }}</TIcon>
      </div>
    </template>
    <template #content="slotProps">
      <div
        class="tw:w-full tw:text-start tw:px-4 tw:py-3 tw:mr-3 tw:mb-6! tw:rounded-lg"
        :class="
          getState(slotProps.item) === 'current'
            ? 'tw:bg-success-500/20'
            : 'tw:bg-zinc-500/20'
        "
      >
        <TCardTitle class="tw:p-0!">
          <div class="tw:flex tw:flex-col tw:w-full">
            <div class="tw:flex tw:justify-between tw:items-start">
              <h2
                class="tw:text-base tw:mr-2"
                :class="
                  getState(slotProps.item) === 'current'
                    ? 'tw:text-success-600 tw:dark:text-success-300/90'
                    : getState(slotProps.item) === 'past'
                      ? 'tw:text-zinc-500/50'
                      : 'tw:text-zinc-600 tw:dark:text-zinc-400'
                "
              >
                <span v-if="slotProps.item.start">
                  {{ datetimeWithDayOfWeek(slotProps.item.start) }}
                </span>
                <span
                  class="tw:px-2"
                  v-if="slotProps.item.start && slotProps.item.end"
                >
                  &rarr;
                </span>
                <span v-if="slotProps.item.end">
                  {{ datetimeWithDayOfWeek(slotProps.item.end) }}
                </span>
              </h2>
              <TChip
                v-if="getState(slotProps.item) === 'current'"
                dense
                class="tw:text-xs tw:bg-success-400/50 tw:text-success-800 tw:dark:text-success-200"
              >
                Now
              </TChip>
              <TChip
                v-if="getState(slotProps.item) === 'past'"
                dense
                class="tw:text-xs tw:bg-zinc-500/30 tw:text-zinc-600 tw:dark:text-zinc-400"
              >
                Past
              </TChip>
            </div>

            <span
              class="tw:text-xl tw:pt-1"
              :class="
                getState(slotProps.item) === 'past' ? 'tw:text-zinc-500' : ''
              "
            >
              {{ slotProps.item.name }}
            </span>
          </div>
        </TCardTitle>
        <TCardText
          class="tw:px-0 tw:pb-2 tw:text-base"
          v-if="getState(slotProps.item) !== 'past' && !isOtherBuyer"
        >
          <div v-html="slotProps.item.description" />
        </TCardText>
        <div
          class="tw:flex tw:gap-2 tw:flex-wrap"
          :class="filterActions(slotProps.item).length > 0 ? 'tw:mt-3' : ''"
        >
          <TBtn
            v-for="action in filterActions(slotProps.item)"
            :key="action.text"
            :disabled="
              !isAuthed ||
              isFutureMilestone(slotProps.item) ||
              (action.isSpecialWorkaroundForRfpAnalysisPage &&
                isCurrentMilestone(slotProps.item))
            "
            :color="isCurrentMilestone(slotProps.item) ? 'green' : 'gray'"
            :to="action.link"
            :flat="false"
            :router-link-props="action.routerLinkProps ?? {}"
            @click="handleOnClick(action.onClick)"
          >
            {{
              getState(slotProps.item) === "past" && action.pastText
                ? action.pastText
                : action.text
            }}
            <TIcon right>
              {{ action.icon }}
            </TIcon>
          </TBtn>
        </div>
      </div>
    </template>
  </Timeline>
</template>
